"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import * as Icon from "@phosphor-icons/react/dist/ssr";
import { ProductType } from "@/type/ProductType";
import Product from "../Product/Product";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import HandlePagination from "../Other/HandlePagination";
import { useNavigate } from "react-router-dom";


interface Props {
  data: Array<ProductType>;
  productPerPage: number;
  dataType: string | null;
}

const ShopBreadCrumbImg: React.FC<Props> = ({
  data,
  productPerPage,
  dataType,
}) => {
  const [layoutCol, setLayoutCol] = useState<number | null>(4);
  const [showOnlySale, setShowOnlySale] = useState(false);
  const [sortOption, setSortOption] = useState("");
  const [openSidebar, setOpenSidebar] = useState(false);
  const [type, setType] = useState<string | null>(dataType);
  const [size, setSize] = useState<string | null>();
  const [color, setColor] = useState<string | null>();
  const [brand, setBrand] = useState<string | null>();
  const [priceRange, setPriceRange] = useState<{ min: number; max: number }>({
    min: 0,
    max: 50000,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const productsPerPage = productPerPage;
  const router = useNavigate();
  const shopProductsTopRef = useRef<HTMLDivElement>(null);
  const prevEffectivePageRef = useRef<number | null>(null);

  // Category mapping from frontend to database
  const categoryMapping: { [key: string]: string[] } = {
    "PYJAMAS ET GANDORA": ["PYJAMAS", "GANDORA", "PYJAMA", "PYJAMAS- FEMME", "PYJAMAS- HOMME"],
    "CHALES & FOULARDS": ["CHALE", "FOULARDS", "FOULARD", "CHALES", "ÉCHARPES"],
    "LINGERIE ET SOUS-VETEMENTS": ["LINGERIE", "SOUS-VETEMENTS", "LINGERIE-FEMME", "SS VET/ FEMME"],
    "ACCESSOIRES HAMMAM": ["ACCESSOIRES HAMMAM", "ACCESS /BAIN", "HAMMAM", "SOIN BEAUTE"],
    "BIJOUX": ["BIJOUX", "BIJOUX FANTASIE", "PLAQUE OR", "ARGENT"],
    "LINGE DE MAISON": ["LINGE DE MAISON", "LINGE", "MAISON", "VAISSELLE", "SERVIETTES"],
    "ACCESSOIRES": ["ACCESSOIRES PLAGE", "PLAGE", "MAILLOTS", "PARASOLS"],
    "DIVERS": ["DIVERS", "AUTRES", "MISC", "VARIÉS"]
  };

  const handleLayoutCol = (col: number) => {
    setLayoutCol(col);
  };



  const handleOpenSidebar = () => {
    setOpenSidebar((toggleOpen) => !toggleOpen);
    setCurrentPage(0);
  };

  const handleType = (type: string) => {
      router(`/shop?category=${encodeURIComponent(type)}`);
  };



  const handlePriceChange = (values: number | number[]) => {
    if (Array.isArray(values)) {
      setPriceRange({ min: values[0], max: values[1] });
      setCurrentPage(0);
    }
  };

  

  // Filter product
  let filteredData = data.filter((product) => {
    // Ensure safe fallback values
    const category = product.category || "";
    const price = Number(product.price) || 0;
  
    // Filter by dataType (category) with flexible matching
    let isDataTypeMatched = true;
    if (dataType) {
      // Create a mapping for category matching
      const categoryMatches: { [key: string]: string[] } = {
        "PYJAMAS ET GANDORA": ["PYJAMAS- FEMME"],
        "PYJAMAS & GANDORA": ["PYJAMAS- FEMME"],
        "CHALES & FOULARDS": ["CHALES FOULARDS"],
        "CHALE & FOULARDS": ["CHALES FOULARDS"],
        "CHALE & FOULARD": ["CHALES FOULARDS"],
        "LINGERIE ET SOUS-VETEMENTS": ["LINGERIE-FEMME"],
        "ACCESSOIRES HAMMAM": ["ACCESS /BAIN"],
        "ACCESSOIRES CHEVEUX": ["ACCESS /BAIN"],
        "BIJOUX": ["BIJOUX"],
        "LINGE DE MAISON": ["LINGE DE MAISON"],
        "Accessoire cheveux": ["ACCESSOIRES"],
        "ACCESSOIRES": ["ACCESSOIRES"],
        /** Do not include "ACCESSOIRES" here — it overlaps the ACCESSOIRES category (plage, etc.). */
        "DIVERS": ["DIVERS", "AUTRES", "MISC", "VARIÉS", "SERVIETTES", "SOIN BEAUTE", "CHAUSSETTES", "VAISSELLE", "CHAUSSURES", "VETEMENTS"]
      };
      
      const mappedCategories = categoryMatches[dataType] || [dataType];
      isDataTypeMatched = mappedCategories.some(mappedCat => 
        category.toLowerCase().includes(mappedCat.toLowerCase()) ||
        mappedCat.toLowerCase().includes(category.toLowerCase())
      );
    }
  
    // Filter by "on sale"
    const isShowOnlySaleMatched = !showOnlySale || !!product.sale;
  
    // Filter by price range
    const isPriceRangeMatched =
      (priceRange.min === 0 && priceRange.max === 50000) ||
      (price >= priceRange.min && price <= priceRange.max);
  
    return (
      isDataTypeMatched &&
      isShowOnlySaleMatched &&
      isPriceRangeMatched
    );
  });
  

  // Create a copy array filtered to sort
  let sortedData = [...filteredData];

  if (sortOption === "soldQuantityHighToLow") {
    filteredData = sortedData.sort((a, b) => b.sold - a.sold);
  }

  if (sortOption === "discountHighToLow") {
    const discPct = (p: ProductType) => {
      const o = Number(p.originPrice) || 0;
      if (o <= 0) return 0;
      return Math.floor(100 - (Number(p.price) / o) * 100);
    };
    filteredData = sortedData.sort((a, b) => discPct(b) - discPct(a));
  }

  if (sortOption === "priceHighToLow") {
    filteredData = sortedData.sort((a, b) => b.price - a.price);
  }

  if (sortOption === "priceLowToHigh") {
    filteredData = sortedData.sort((a, b) => a.price - b.price);
  }

  const totalProducts = filteredData.length;
  const selectedType = type;
  const selectedSize = size;
  const selectedColor = color;
  const selectedBrand = brand;

  if (filteredData.length === 0) {
    filteredData = [
      {
        id: "no-data",
        category: "no-data",
        type: "no-data",
        name: "no-data",
        gender: "no-data",
        new: false,
        sale: false,
        rate: 0,
        price: 0,
        originPrice: 0,
        brand: "no-data",
        sold: 0,
        quantity: 0,
        quantityPurchase: 0,
        sizes: [],
        variation: [],
        thumbImage: [],
        images: [],
        description: "no-data",
        action: "no-data",
        slug: "no-data",
      },
    ];
  }

  // Find page number base on filteredData
  const pageCount = Math.ceil(filteredData.length / productsPerPage) || 1;
  const maxPageIndex = Math.max(0, pageCount - 1);

  /** When filters/category change, last page index can shrink — clamp to avoid empty slice */
  const effectivePage = Math.min(currentPage, maxPageIndex);
  const offsetSafe = effectivePage * productsPerPage;

  /** Category changes remount this component (key on parent) — only clamp when filters shrink the page count. */
  useLayoutEffect(() => {
    setCurrentPage((prev) => (prev > maxPageIndex ? maxPageIndex : prev));
  }, [maxPageIndex]);

  /** After pagination changes, jump the viewport to the product grid (instant, no smooth scroll). Fixes mobile staying at bottom / “blank” area. */
  useLayoutEffect(() => {
    const prev = prevEffectivePageRef.current;
    prevEffectivePageRef.current = effectivePage;
    if (prev === null || prev === effectivePage) return;
    shopProductsTopRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
  }, [effectivePage]);

  // Get product data for current page
  let currentProducts: ProductType[];

  if (filteredData.length > 0) {
    currentProducts = filteredData.slice(offsetSafe, offsetSafe + productsPerPage);
  } else {
    currentProducts = [];
  }

  const handlePageChange = (selected: number) => {
    const idx = Number(selected);
    if (!Number.isFinite(idx)) return;
    setCurrentPage(Math.min(Math.max(0, idx), maxPageIndex));
  };

  const handleClearAll = () => {
    setSortOption("");
    setType(null);
    setSize(null);
    setColor(null);
    setBrand(null);
    setPriceRange({ min: 0, max: 50000 });
    setCurrentPage(0);
    router("/shop");
  };

  

  return (
    <>
      <div className="breadcrumb-block w-full">
        <div className="breadcrumb-main bg-pink w-full shadow-lg">
          <div className="w-full py-2 md:py-3 px-4 md:px-8">
            <div className="max-w-7xl mx-auto flex flex-col items-center justify-center">
              <div className="text-content w-full flex items-center justify-center gap-4 md:gap-6">
                <div className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                  {dataType === null ? "Boutique" : dataType}
                </div>
                <div className="flex items-center justify-center gap-2 text-sm md:text-base text-white font-medium">
                  <a href={"/"} className="hover:text-pink-200 transition-colors">Accueil</a>
                  <Icon.CaretRight size={16} className="font-semibold" />
                  <div className="font-semibold capitalize">
                    {dataType === null ? "Boutique" : dataType}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Categories Section */}
      <div className="categories-section bg-white border-b border-pink shadow-sm">
        <div className="container px-4 md:px-8 py-3">
          <div className="max-w-7xl mx-auto">
            <div className="categories-grid flex flex-wrap justify-center gap-2 md:gap-3">
                {[
                  "PYJAMAS ET GANDORA",
                  "CHALES & FOULARDS",
                  "LINGERIE ET SOUS-VETEMENTS",
                  "ACCESSOIRES HAMMAM",
                  "BIJOUX",
                  "ACCESSOIRES",
                  "DIVERS",
                ].map((item, index) => (
                <button
                    key={index}
                  className={`category-btn px-2 py-1 md:px-3 md:py-2 rounded-md text-xs font-medium transition-all duration-200 transform hover:scale-105 active:scale-95 whitespace-nowrap ${
                    dataType === item 
                      ? 'bg-pink text-white shadow-md' 
                      : 'bg-white text-pink hover:bg-pink hover:text-white border border-pink'
                    }`}
                    onClick={() => handleType(item)}
                  >
                    {item}
                </button>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div className="shop-product breadcrumb1 lg:py-20 md:py-14 py-10">
        <div className="container">
          <div className="list-product-block relative">
            <div className="filter-heading flex items-center justify-between gap-5 flex-wrap">
              <div className="left flex has-line items-center flex-wrap gap-5">
                <div
                  className="filter-sidebar-btn flex items-center gap-2 cursor-pointer"
                  onClick={handleOpenSidebar}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M4 21V14"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 10V3"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 21V12"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 8V3"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 21V16"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 12V3"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 14H7"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 8H15"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 16H23"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Filtres</span>
                </div>
                <div className="choose-layout flex items-center gap-2">
                  <div
                    className={`item three-col p-2 border border-line rounded flex items-center justify-center cursor-pointer ${
                      layoutCol === 3 ? "active" : ""
                    }`}
                    onClick={() => handleLayoutCol(3)}
                  >
                    <div className="flex items-center gap-0.5">
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                    </div>
                  </div>
                  <div
                    className={`item four-col p-2 border border-line rounded flex items-center justify-center cursor-pointer ${
                      layoutCol === 4 ? "active" : ""
                    }`}
                    onClick={() => handleLayoutCol(4)}
                  >
                    <div className="flex items-center gap-0.5">
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                    </div>
                  </div>
                  <div
                    className={`item five-col p-2 border border-line rounded flex items-center justify-center cursor-pointer ${
                      layoutCol === 5 ? "active" : ""
                    }`}
                    onClick={() => handleLayoutCol(5)}
                  >
                    <div className="flex items-center gap-0.5">
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                      <span className="w-[3px] h-4 bg-secondary2 rounded-sm"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`sidebar style-dropdown bg-white grid md:grid-cols-4 grid-cols-2 md:gap-[30px] gap-6 ${
                openSidebar ? "open" : ""
              }`}
              style={{
                padding: "28px",
              }}
            >
              
              <div>
                <div className="filter-price mt-8">
                  <div className="heading6">Tranche de Prix</div>
                  <Slider
                    range
                    defaultValue={[0, 50000]}
                    min={0}
                    max={50000}
                    onChange={handlePriceChange}
                    className="mt-5"
                  />
                  <div className="price-block flex items-center justify-between flex-wrap mt-4">
                    <div className="min flex items-center gap-1">
                      <div>Prix Min:</div>
                      <div className="price-min">
                        MAD <span>{priceRange.min}</span>
                      </div>
                    </div>
                    <div className="min flex items-center gap-1">
                      <div>Prix Max:</div>
                      <div className="price-max">
                        MAD <span>{priceRange.max}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="list-filtered flex items-center gap-3 mt-4">
              <div className="total-product">
                {totalProducts}
                <span className="text-secondary pl-1">Produits trouvés</span>
              </div>
              {(selectedType ||
                selectedSize ||
                selectedColor ||
                selectedBrand) && (
                <>
                  <div className="list flex items-center gap-3">
                    <div className="w-px h-4 bg-line"></div>
                    {selectedType && (
                      <div
                        className="item flex items-center px-2 py-1 gap-1 bg-linear rounded-full capitalize"
                        onClick={() => {
                          setType(null);
                        }}
                      >
                        <Icon.X className="cursor-pointer" />
                        <span>{selectedType}</span>
                      </div>
                    )}
                    {selectedSize && (
                      <div
                        className="item flex items-center px-2 py-1 gap-1 bg-linear rounded-full capitalize"
                        onClick={() => {
                          setSize(null);
                        }}
                      >
                        <Icon.X className="cursor-pointer" />
                        <span>{selectedSize}</span>
                      </div>
                    )}
                    {selectedColor && (
                      <div
                        className="item flex items-center px-2 py-1 gap-1 bg-linear rounded-full capitalize"
                        onClick={() => {
                          setColor(null);
                        }}
                      >
                        <Icon.X className="cursor-pointer" />
                        <span>{selectedColor}</span>
                      </div>
                    )}
                    {selectedBrand && (
                      <div
                        className="item flex items-center px-2 py-1 gap-1 bg-linear rounded-full capitalize"
                        onClick={() => {
                          setBrand(null);
                        }}
                      >
                        <Icon.X className="cursor-pointer" />
                        <span>{selectedBrand}</span>
                      </div>
                    )}
                  </div>
                  <div
                    className="clear-btn flex items-center px-2 py-1 gap-1 rounded-full border border-red cursor-pointer"
                    onClick={handleClearAll}
                  >
                    <Icon.X
                      color="rgb(219, 68, 68)"
                      className="cursor-pointer"
                    />
                    <span className="text-button-uppercase text-red">
                      Effacer tout
                    </span>
                  </div>
                </>
              )}
            </div>

            <div
              ref={shopProductsTopRef}
              className="shop-products-scroll-anchor h-px w-full shrink-0 scroll-mt-4 md:scroll-mt-6"
              aria-hidden
            />
            <div
              className={`list-product hide-product-sold grid sm:grid-cols-3 grid-cols-2 sm:gap-[30px] gap-[20px] mt-7 ${
                layoutCol === 3
                  ? "lg:grid-cols-3"
                  : layoutCol === 5
                    ? "lg:grid-cols-5"
                    : "lg:grid-cols-4"
              }`}
            >
              {currentProducts.map((item) =>
                item.id === "no-data" ? (
                  <div key={item.id} className="no-data-product">
                    Aucun produit ne correspond aux critères sélectionnés.
                  </div>
                ) : (
                  <Product key={item.id} data={item} type="grid" />
                )
              )}
            </div>

            {pageCount > 1 && (
              <div className="list-pagination flex items-center justify-center md:mt-10 mt-7">
                <HandlePagination
                  key={`pager-${pageCount}-${dataType ?? "all"}`}
                  pageCount={pageCount}
                  onPageChange={handlePageChange}
                  forcePage={effectivePage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShopBreadCrumbImg;
